import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { postStage, subscribeStage, isProjectorWindow } from '@/lib/stageChannel';
import { Smartphone, EyeOff, Eye } from 'lucide-react';

// ── Screen curtain ───────────────────────────────────────────────────────────
// Privacy for what the ROOM sees, toggled from any admin page. Two modes:
//  · Projector window open (extended display): the curtain applies to the
//    projector window only — the admin's own screen stays completely normal.
//  · No projector window (mirrored display): fall back to covering this
//    window, since both screens show the same thing anyway.
// The placeholder video lives at a fixed public-storage path (uploaded from
// the Curtain screen card); without one a polite prompt shows instead.
export const CURTAIN_BUCKET = 'message-attachments';
export const CURTAIN_PATH = 'demo-curtain';
export const CURTAIN_FILE = 'curtain.mp4';

const PROJECTOR_ALIVE_MS = 8000;

export const openCurtain = () =>
  window.dispatchEvent(new CustomEvent('parcelink:curtain', { detail: { show: true } }));
export const toggleCurtain = () =>
  window.dispatchEvent(new CustomEvent('parcelink:curtain', { detail: { toggle: true } }));

/** Public URL of the uploaded curtain video, or null when none exists.
 *  updated_at rides along as a cache-buster so a re-upload shows immediately. */
export async function fetchCurtainVideoUrl() {
  try {
    const { data, error } = await supabase.storage.from(CURTAIN_BUCKET).list(CURTAIN_PATH);
    if (error) return null;
    const file = (data || []).find((f) => f.name === CURTAIN_FILE);
    if (!file) return null;
    const { data: pub } = supabase.storage.from(CURTAIN_BUCKET).getPublicUrl(`${CURTAIN_PATH}/${CURTAIN_FILE}`);
    if (!pub?.publicUrl) return null;
    const stamp = encodeURIComponent(file.updated_at || file.created_at || '');
    return `${pub.publicUrl}?v=${stamp}`;
  } catch {
    return null;
  }
}

/**
 * What the curtain should show right now, personalisation included:
 * the live session's generated curtain (video, else still) beats the global
 * uploaded file; everything fails through to the written prompt. Shaped as
 * { video, still, company } — company is overlaid as HTML type because the
 * generators are never asked to paint text.
 */
export async function fetchCurtainMedia() {
  try {
    const { data: session } = await supabase.rpc('get_active_demo_session');
    const title = session?.title?.trim();
    if (title) {
      const slug = title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
      const { data: row } = await supabase
        .from('demo_curtains')
        .select('status, still_url, video_url')
        .eq('slug', slug)
        .maybeSingle();
      if (row?.video_url) return { video: row.video_url, still: row.still_url || null, company: title, live: true };
      if (row?.still_url) return { video: null, still: row.still_url, company: title, live: true };
    }
    if (session?.id) {
      const video = await fetchCurtainVideoUrl();
      return { video, still: null, company: null, live: true };
    }
  } catch { /* fall through to the global file */ }
  const video = await fetchCurtainVideoUrl();
  return { video, still: null, company: null, live: false };
}

/**
 * Seamless loop: generated clips are short (~5s) and a native `loop` shows a
 * visible hiccup at the seam. Two stacked players take turns — half a second
 * before one ends, the other starts from zero and crossfades in over it.
 */
const SeamlessLoopVideo = ({ src }) => {
  const a = useRef(null);
  const b = useRef(null);
  const [front, setFront] = useState(0); // which player is on top
  const swapping = useRef(false);

  useEffect(() => {
    setFront(0);
    swapping.current = false;
    const va = a.current;
    if (va) { va.currentTime = 0; va.play?.().catch(() => {}); }
  }, [src]);

  const onTime = (idx) => (e) => {
    if (idx !== front || swapping.current) return;
    const v = e.currentTarget;
    if (!v.duration || v.duration - v.currentTime > 0.5) return;
    swapping.current = true;
    const other = (idx === 0 ? b : a).current;
    if (other) { other.currentTime = 0; other.play?.().catch(() => {}); }
    setFront(idx === 0 ? 1 : 0);
    setTimeout(() => { swapping.current = false; }, 700);
  };

  const cls = (idx) =>
    `absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${front === idx ? 'opacity-100' : 'opacity-0'}`;
  return (
    <>
      <video ref={a} src={src} className={cls(0)} autoPlay muted playsInline onTimeUpdate={onTime(0)} />
      <video ref={b} src={src} className={cls(1)} muted playsInline onTimeUpdate={onTime(1)} />
    </>
  );
};

/** The company name, as crisp type dead-centre over the generated backdrop —
 *  the generators are never asked to paint text, so it can't come out
 *  mangled. A soft radial scrim keeps white type legible on a bright image. */
const CompanyOverlay = ({ company }) => !company ? null : (
  <div className="pointer-events-none absolute inset-0 grid place-items-center px-8">
    <div
      className="flex flex-col items-center gap-3 rounded-[48px] px-16 py-12 text-center"
      style={{ background: 'radial-gradient(closest-side, rgba(2,8,23,0.45), rgba(2,8,23,0.25) 60%, transparent)' }}
    >
      <p className="text-[12px] font-semibold uppercase tracking-[0.4em]" style={{ color: 'rgba(255,255,255,0.75)' }}>
        Prepared for
      </p>
      <p className="max-w-4xl text-4xl font-semibold tracking-tight sm:text-6xl" style={{ color: '#ffffff', textShadow: '0 4px 34px rgba(0,0,0,0.65)' }}>
        {company}
      </p>
    </div>
  </div>
);

/** The visual the room sees while hidden — shared by the local fallback
 *  overlay and the projector window. Accepts the fetchCurtainMedia() shape
 *  via `media`; plain `videoUrl` keeps every existing caller working. */
export const CurtainFace = ({ videoUrl, media }) => {
  const video = media?.video ?? videoUrl ?? null;
  const still = media?.still ?? null;
  const company = media?.company ?? null;
  if (video) {
    return (
      <>
        {company ? <SeamlessLoopVideo src={video} /> : (
          <video src={video} className="absolute inset-0 h-full w-full object-cover" autoPlay loop muted playsInline />
        )}
        <CompanyOverlay company={company} />
      </>
    );
  }
  if (still) {
    return (
      <>
        {/* Ken Burns drift while the motion pass renders — personalised from second one. */}
        <motion.img
          src={still}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.06, x: '-1.5%' }}
          animate={{ scale: 1.14, x: '1.5%' }}
          transition={{ duration: 28, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }}
        />
        <CompanyOverlay company={company} />
      </>
    );
  }
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-8 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 text-white">
        <Smartphone className="h-10 w-10" />
      </div>
      <h2 className="max-w-2xl text-2xl font-semibold !text-white sm:text-4xl" style={{ color: '#ffffff' }}>
        Eyes on your own device
      </h2>
      <p className="max-w-xl text-lg font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
        Please continue on your demo dashboard — follow the scene prompt at the
        bottom of your screen. The big screen will be back in a moment.
      </p>
    </div>
  );
};

const ScreenCurtain = () => {
  const [on, setOn] = useState(false); // the logical curtain state (room-facing)
  const [media, setMedia] = useState(null);
  const lastBeat = useRef(0);
  const onRef = useRef(false);
  onRef.current = on;

  const projectorAlive = () => Date.now() - lastBeat.current < PROJECTOR_ALIVE_MS;
  const [, forceTick] = useState(0); // re-render so the local overlay reacts to beats

  const setCurtain = (next) => {
    setOn(next);
    postStage({ type: 'curtain', show: next });
    if (next) fetchCurtainMedia().then(setMedia);
  };

  useEffect(() => {
    const onEvent = (e) => {
      const next = e?.detail?.toggle ? !onRef.current : e?.detail?.show !== false;
      setCurtain(next);
    };
    window.addEventListener('parcelink:curtain', onEvent);
    const unsub = subscribeStage((msg) => {
      if (msg?.type === 'beat' || msg?.type === 'hello') {
        lastBeat.current = Date.now();
        forceTick((t) => t + 1);
        // A freshly opened projector syncs to the current curtain state.
        if (msg.type === 'hello') postStage({ type: 'curtain', show: onRef.current });
      }
      // THE PROJECTOR WINDOW obeys the presenter's curtain wherever it has
      // navigated. On a live scene this window is a real admin page — the
      // ProjectorStagePage handler that used to catch this message is
      // unmounted — so until 14 Oct "Hide screen" did nothing on live scenes.
      // Set state directly (no re-post): BroadcastChannel never echoes to the
      // sender, so this can't loop with the presenter's window.
      if (msg?.type === 'curtain' && isProjectorWindow()) {
        setOn(!!msg.show);
        if (msg.show) fetchCurtainMedia().then(setMedia);
      }
    });
    return () => { window.removeEventListener('parcelink:curtain', onEvent); unsub(); };
  }, []);

  useEffect(() => {
    // Esc lifts the curtain — but never FROM the projector window, where a
    // stray keypress on the room's screen would undo the presenter's hide.
    if (!on || isProjectorWindow()) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCurtain(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [on]);

  // Local fallback overlay only when no projector window is carrying the
  // curtain — with one alive, the admin screen stays normal. The projector
  // window ITSELF always covers when told to: it never hears its own beats,
  // and it IS the room's screen.
  const projectorSelf = isProjectorWindow();
  const showLocal = on && (projectorSelf || !projectorAlive());

  return (
    <>
      {on && !projectorSelf && projectorAlive() && (
        <button
          type="button"
          onClick={() => setCurtain(false)}
          className="fixed bottom-4 right-4 z-[105] flex items-center gap-2 rounded-full border border-primary/40 bg-slate-950/90 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-2xl backdrop-blur-md hover:bg-slate-900"
        >
          <EyeOff className="h-4 w-4 text-primary" /> Projector hidden — click to show
        </button>
      )}
      <AnimatePresence>
        {showLocal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // During a LIVE session the curtain sits UNDER the nav pad (z-70)
            // so the presenter keeps the scene controls on top of it, and
            // click-anywhere no longer dismisses — the pad owns the clicks.
            // Outside a session it stays the full privacy blanket it was.
            // On the PROJECTOR window it covers everything, keeps no presenter
            // caption (the room reads this screen) and never click-dismisses —
            // the presenter lifts it from their own window.
            className={`fixed inset-0 ${projectorSelf ? 'z-[110]' : media?.live ? 'z-[64]' : 'z-[110] cursor-pointer'} overflow-hidden bg-slate-950`}
            onClick={projectorSelf || media?.live ? undefined : () => setCurtain(false)}
            role="dialog"
            aria-label="Screen hidden"
          >
            <CurtainFace media={media} />
            {!projectorSelf && (
            <p className={`absolute ${media?.live ? 'bottom-28' : 'bottom-4'} left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-sm`} style={{ color: 'rgba(255,255,255,0.75)' }}>
              {media?.live
                ? 'Presenter: drive scenes from the pad — Esc shows the screen'
                : 'Presenter: click anywhere or press Esc to show the screen'}
            </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/** Header toggle — always in reach, on every admin page. Lit while the
 *  curtain is up so the presenter can drop/raise it from anywhere. */
export const CurtainToggleButton = () => {
  const [lit, setLit] = useState(false);
  useEffect(() => {
    const unsub = subscribeStage((msg) => {
      if (msg?.type === 'curtain') setLit(!!msg.show);
    });
    const onEvent = (e) => {
      if (e?.detail?.toggle) return; // resolved by ScreenCurtain, echoed via channel
      setLit(e?.detail?.show !== false);
    };
    window.addEventListener('parcelink:curtain', onEvent);
    return () => { unsub(); window.removeEventListener('parcelink:curtain', onEvent); };
  }, []);
  return (
    <button
      type="button"
      onClick={toggleCurtain}
      aria-label={lit ? 'Show the screen to the room' : 'Hide the screen from the room'}
      title={lit ? 'Show screen' : 'Hide screen'}
      className={`grid h-9 w-9 place-items-center rounded-xl border transition-colors ${
        lit
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
      }`}
    >
      {lit ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
    </button>
  );
};

export default ScreenCurtain;
